/* eslint-disable react/prop-types */
import React, { useState, useCallback, memo } from 'react';
import { useTheme } from '../../../../Context/ThemeContext';
import { navItems, NavItem } from '../navConfig';
import { useNavigation } from '../hooks';
import { DarkModeToggle } from './DarkModeToggle';
import { SidebarAnimations } from './SidebarAnimations';
import mainLogo from '../../../../Assests/images/NavBarLogo.png';

interface MobileSidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (value: boolean) => void;
  expandedItems: Record<string, boolean>;
  setExpandedItems: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

export const MobileSidebar: React.FC<MobileSidebarProps> = memo(({
  sidebarOpen,
  setSidebarOpen,
  expandedItems,
  setExpandedItems,
}) => {
  const { theme, darkMode, toggleDarkMode } = useTheme();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [hoveredChild, setHoveredChild] = useState<string | null>(null);

  const { toggleExpanded, handleNavigate, isActive, isChildActive } = useNavigation({
    isMobile: true,
    setSidebarOpen,
    setExpandedItems,
  });

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, action: () => void) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        action();
      }
    },
    []
  );

  const handleNavItemClick = useCallback(
    (item: NavItem) => {
      if (item.children) {
        toggleExpanded(item.label);
      } else if (item.path) {
        handleNavigate(item.path);
      }
    },
    [toggleExpanded, handleNavigate]
  );

  const handleOverlayClick = useCallback(() => {
    setSidebarOpen(false);
  }, [setSidebarOpen]);

  const handleOverlayKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSidebarOpen(false);
      }
    },
    [setSidebarOpen]
  );

  const getNavItemStyle = useCallback(
    (item: NavItem, isHovered: boolean): React.CSSProperties => {
      const active = isActive(item);
      return {
        padding: '12px 16px',
        margin: '2px 8px',
        borderRadius: '6px',
        color: '#fff',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        background: active ? '#2563eb' : isHovered ? 'rgba(255,255,255,0.05)' : 'transparent',
        fontSize: '14px',
        transition: 'all 0.2s ease',
        border: 'none',
        width: 'calc(100% - 16px)',
        textAlign: 'left',
      };
    },
    [isActive]
  );

  const getChildItemStyle = useCallback(
    (path: string, isHovered: boolean, isExpanded: boolean): React.CSSProperties => {
      const active = isChildActive(path);
      return {
        padding: '10px 16px 10px 32px',
        margin: '2px 8px',
        borderRadius: '6px',
        color: active ? '#fff' : '#94a3b8',
        background: active ? 'rgba(37,99,235,0.5)' : isHovered ? 'rgba(255,255,255,0.05)' : 'transparent',
        cursor: 'pointer',
        fontSize: '13px',
        transition: 'all 0.2s ease',
        opacity: isExpanded ? 1 : 0,
        transform: isExpanded ? 'translateX(0)' : 'translateX(-10px)',
        border: 'none',
        width: 'calc(100% - 16px)',
        textAlign: 'left',
      };
    },
    [isChildActive]
  );

  return (
    <>
      {sidebarOpen && (
        <div
          onClick={handleOverlayClick}
          onKeyDown={handleOverlayKeyDown}
          role="button"
          tabIndex={0}
          aria-label="Close sidebar"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 999,
            animation: 'sidebarFadeIn 0.3s ease',
          }}
        />
      )}
      <aside
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          width: '240px',
          height: '100vh',
          background: theme.sidebar,
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1000,
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: sidebarOpen ? '4px 0 12px rgba(0,0,0,0.3)' : 'none',
        }}
        role="navigation"
        aria-label="Mobile navigation"
        aria-hidden={!sidebarOpen}
      >
        <div style={{
          padding: '16px',
          borderBottom: `1px solid ${theme.border}`,
          flexShrink: 0,
          animation: sidebarOpen ? 'sidebarFadeIn 0.4s ease 0.2s backwards' : 'none',
        }}>
          <img src={mainLogo} alt="Logo" style={{ height: '36px', objectFit: 'contain' }} />
        </div>
        <nav
          style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}
          aria-label="Main navigation"
        >
          {navItems.map((item, index) => (
            <div
              key={item.label}
              style={{
                animation: sidebarOpen ? `sidebarSlideInLeft 0.3s ease ${0.1 + index * 0.05}s backwards` : 'none',
              }}
            >
              <button
                onClick={() => handleNavItemClick(item)}
                onKeyDown={(e) => handleKeyDown(e, () => handleNavItemClick(item))}
                onTouchStart={() => setHoveredItem(item.label)}
                onTouchEnd={() => setHoveredItem(null)}
                onMouseEnter={() => setHoveredItem(item.label)}
                onMouseLeave={() => setHoveredItem(null)}
                aria-expanded={item.children ? expandedItems[item.label] : undefined}
                aria-haspopup={item.children ? 'true' : undefined}
                style={getNavItemStyle(item, hoveredItem === item.label)}
              >
                {item.label}
                {item.children && (
                  <span
                    style={{
                      fontSize: '10px',
                      transition: 'transform 0.3s ease',
                      transform: expandedItems[item.label] ? 'rotate(180deg)' : 'rotate(0deg)',
                      display: 'inline-block',
                    }}
                    aria-hidden="true"
                  >
                    ▼
                  </span>
                )}
              </button>
              {item.children && (
                <div
                  style={{
                    maxHeight: expandedItems[item.label] ? '500px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  role="menu"
                  aria-label={`${item.label} submenu`}
                >
                  {item.children.map((child, childIndex) => (
                    <button
                      key={child.path}
                      onClick={() => handleNavigate(child.path)}
                      onKeyDown={(e) => handleKeyDown(e, () => handleNavigate(child.path))}
                      onTouchStart={() => setHoveredChild(child.path)}
                      onTouchEnd={() => setHoveredChild(null)}
                      onMouseEnter={() => setHoveredChild(child.path)}
                      onMouseLeave={() => setHoveredChild(null)}
                      role="menuitem"
                      style={{
                        ...getChildItemStyle(child.path, hoveredChild === child.path, !!expandedItems[item.label]),
                        transitionDelay: expandedItems[item.label] ? `${childIndex * 0.05}s` : '0s',
                      }}
                    >
                      {child.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        <div style={{
          padding: '16px',
          borderTop: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          animation: sidebarOpen ? 'sidebarFadeIn 0.4s ease 0.3s backwards' : 'none',
        }}>
          <span style={{ color: '#fff', fontSize: '13px' }}>Dark Mode</span>
          <DarkModeToggle darkMode={darkMode} toggleDarkMode={toggleDarkMode} includeLabel={false} />
        </div>

        <SidebarAnimations />
      </aside>
    </>
  );
});

MobileSidebar.displayName = 'MobileSidebar';
